/* eslint-disable promise/prefer-await-to-then */
import type {ExtensionAPI, SnapshotDal} from '@wix/document-manager-core'
import {getReportableFromError} from '@wix/document-manager-utils'
import type {
    AppDefinitionId,
    AppsInstallStateMap,
    Callback,
    Callback1,
    ClientSpecMap,
    PS
} from '@wix/document-services-types'
import _ from 'lodash'
import dsConstants from '../../constants/constants'
import editorServerFacade from '../../editorServerFacade/editorServerFacade'
import constants from '../../platform/common/constants'
import platform from '../../platform/platform'
import clientSpecMapMetaData from '../../siteMetadata/clientSpecMap'
import callbackUtils from '../../utils/callbackUtils'
import {contextAdapter} from '../../utils/contextAdapter'
import {getMetaSiteId} from '../../utils/dalUtil'
import * as visitableData from '../../utils/visitableData'
import provisionUtils from '../utils/provisionUtils'
import queue from '../utils/queue'
import clientSpecMapService from './clientSpecMapService'
import installedTpaAppsOnSiteService from './installedTpaAppsOnSiteService'
import pendingAppsService from './pendingAppsService'

const {getOnSuccessWithFallback} = callbackUtils
const {createQueue} = queue

const context = {
    save: 'save',
    load: 'load',
    firstSave: 'firstSave'
} as const

async function getSettleActions(
    extensionAPI: ExtensionAPI,
    currentPagesVisitable,
    filteredClientSpecMap,
    routerConfigMap,
    semanticAppVersions,
    appsInstallState,
    shouldAvoidRevoking: boolean
) {
    return getSettleActionsFromAllPages(
        extensionAPI,
        currentPagesVisitable,
        filteredClientSpecMap,
        routerConfigMap,
        semanticAppVersions,
        appsInstallState,
        shouldAvoidRevoking
    )
}

const getSettleActionsForFullSave = async (
    extensionAPI: ExtensionAPI,
    {currentPagesVisitable, clientSpecMap, routerConfigMap, semanticAppVersions, appsInstallState},
    shouldAvoidRevoking: boolean = false
) => {
    const filteredClientSpecMap = clientSpecMapService.filterApps(clientSpecMap)
    const actions = await getSettleActions(
        extensionAPI,
        currentPagesVisitable,
        filteredClientSpecMap,
        routerConfigMap,
        semanticAppVersions,
        appsInstallState,
        shouldAvoidRevoking
    )
    contextAdapter.utils.fedopsLogger.interactionStarted(
        dsConstants.PLATFORM_INTERACTIONS.SETTLE_ACTIONS,
        getInteractionsParams(actions)
    )
    return actions
}
const generateUniqueApplicationIdForAllApps = (apps, clientSpecMap) => {
    const uniqueAppIds = []
    _.times(apps.length, () => {
        let newApplicationId = getNewApplicationIdStartingFrom1k(clientSpecMap)
        while (_.includes(uniqueAppIds, newApplicationId)) {
            newApplicationId = getNewApplicationIdStartingFrom1k(clientSpecMap)
        }
        uniqueAppIds.push(newApplicationId)
    })
    return uniqueAppIds
}
const getProvisionAction = (
    clientSpecMap,
    appDefinitionId: AppDefinitionId,
    version: string,
    sourceTemplateId: string,
    applicationId: number
) => {
    const existingApp = _.find(clientSpecMap, {appDefinitionId})

    if (sourceTemplateId) {
        return {
            appDefId: appDefinitionId,
            appVersion: version,
            sourceTemplateId,
            type: 'provisionFromSourceTemplate'
        }
    }
    return {
        appDefId: appDefinitionId,
        applicationId: existingApp?.applicationId || applicationId || getNewApplicationIdStartingFrom1k(clientSpecMap),
        appVersion: version,
        type: 'provision'
    }
}

const getProvisionActions = (clientSpecMap: ClientSpecMap, apps: App[] /** {appDefinitionId, version(optional)} */) => {
    const uniqueAppIds = generateUniqueApplicationIdForAllApps(apps, clientSpecMap)
    return apps.map((app, index) =>
        getProvisionAction(clientSpecMap, app.appDefinitionId, app.version, app.sourceTemplateId, uniqueAppIds[index])
    )
}

const makeSettleRequestWithPS = (
    ps: PS,
    urlData,
    data,
    saveContext: string,
    onSuccess: Callback1<any>,
    onError: Callback1<any>
) => {
    editorServerFacade.sendWithPs(
        ps,
        editorServerFacade.ENDPOINTS.SETTLE_AND_CONDUCT,
        {settle: data},
        getOnSuccessWithFallback('makeSettleRequestWithPS', onError, response => {
            const platformAppsExperiments = {}
            _.forEach(response.experimentsPerAppDefId, (val, key) =>
                _.set(platformAppsExperiments, key, val.appExperiments)
            )
            onSuccess({
                clientSpecMap: response.clientSpecMap,
                platformAppsExperiments,
                blocksExperiments: response.blocksExperiments?.appExperiments || {}
            })
        }),
        error => onError(error),
        urlData.queryString
    )
}

const makeSettleRequestWithPSAsync = (ps: PS, urlData, data, saveContext: string) =>
    new Promise((resolve, reject) => {
        makeSettleRequestWithPS(ps, urlData, data, saveContext, resolve, reject)
    })

const settleQueue = createQueue(makeSettleRequestWithPSAsync)
const queueMakingSettleRequestWithPSAsync = settleQueue.run

const makeSettleRequestWithImmutableSnapshot = (
    immutableSnapshot: SnapshotDal,
    data,
    onSuccess: Callback1<any>,
    onError: Callback1<any>
) => {
    editorServerFacade.sendWithSnapshotDal(
        immutableSnapshot,
        editorServerFacade.ENDPOINTS.SETTLE,
        data,
        getOnSuccessWithFallback('makeSettleRequestWithImmutableSnapshot', onError, response => {
            onSuccess({...response, payload: {clientSpecMap: response.payload}})
        }),
        (error: any) => onError(error)
    )
}

export interface App {
    appDefinitionId: AppDefinitionId
    sourceTemplateId?: string
    version?: string
    appVersion?: string
}

const getAppsVersions = (clientSpecMap, apps: App[]) => {
    const appDefIds = _.map(apps, 'appDefinitionId')
    return _(clientSpecMap)
        .filter(app => appDefIds.includes(app?.appDefinitionId))
        .mapKeys(app => app?.appDefinitionId)
        .mapValues(app => ({
            installedVersion: app?.appFields?.installedVersion,
            semanticVersion: app?.appFields?.semanticVersion
        }))
        .value()
}

const provision = (ps: PS, apps: App[], onSuccess: Function, onError: (e: any) => void) => {
    ps.extensionAPI.logger.interactionStarted('ProvisionThroughServer', {extras: {apps}})
    if (_.isEmpty(apps)) {
        onError('provision must be called with at least one app')
        return
    }

    const branchId = ps.extensionAPI.siteAPI.getBranchId()
    const csm = clientSpecMapMetaData.getAppsData(ps)
    const actions = getProvisionActions(csm, apps)
    const data = _.omitBy(
        {
            actions,
            siteRevision: ps.extensionAPI.siteAPI.getSiteRevision(),
            maybeBranchId: branchId
        },
        _.isNil
    )

    const urlData = {
        appStoreUrl: ps.dal.get(ps.pointers.getInnerPointer(ps.pointers.general.getServiceTopology(), 'appStoreUrl')),
        metaSiteId: getMetaSiteId(ps),
        editorSessionId: ps.siteAPI.getEditorSessionId()
    }

    queueMakingSettleRequestWithPSAsync(ps, urlData, data, context.firstSave)
        .then(
            getOnSuccessWithFallback('appStoreService provision', onError, response => {
                const {platformAppsExperiments, blocksExperiments, clientSpecMap} = response
                ps.extensionAPI.logger.interactionEnded('ProvisionThroughServer', {
                    extras: {
                        versions: getAppsVersions(clientSpecMap, apps)
                    }
                })
                platform.addPlatformAppsExperiments(ps, platformAppsExperiments)
                platform.addBlocksExperiments(ps, blocksExperiments)
                onSuccess(response)
            })
        )
        .catch(onError)
}

const provisionAsync = (ps: PS, apps: App[]): Promise<any> =>
    new Promise<any>((resolve, reject) => {
        provision(ps, apps, resolve, reject)
    })

const update = async (ps: PS, apps: App[], armTags?: Record<string, string>) => {
    if (_.isEmpty(apps)) {
        throw new Error('update must be called with at least one app')
    }

    const branchId = ps.extensionAPI.siteAPI.getBranchId()
    const clientSpecMap = clientSpecMapMetaData.getAppsData(ps)

    const data = _.omitBy(
        {
            actions: apps.map(app => {
                const appData = _.find(clientSpecMap, {appDefinitionId: app.appDefinitionId})

                return {
                    type: 'update',
                    appDefId: appData.appDefinitionId,
                    applicationId: appData.applicationId,
                    instanceId: appData.instanceId,
                    appVersion: app.appVersion
                }
            }),
            siteRevision: ps.extensionAPI.siteAPI.getSiteRevision(),
            maybeBranchId: branchId
        },
        _.isNil
    )

    const urlData = {
        appStoreUrl: ps.dal.get(ps.pointers.getInnerPointer(ps.pointers.general.getServiceTopology(), 'appStoreUrl')),
        metaSiteId: getMetaSiteId(ps),
        editorSessionId: ps.siteAPI.getEditorSessionId(),
        queryString: new URLSearchParams(armTags).toString()
    }

    return queueMakingSettleRequestWithPSAsync(ps, urlData, data, context.firstSave).then(response => {
        return response.clientSpecMap
    })
}

const getInteractionsParams = actions => ({
    extras: {actions: _.map(actions, _.partialRight(_.pick, ['applicationId', 'type']))}
})

const getSettleActionsForSave = async (
    extensionAPI: ExtensionAPI,
    {clientSpecMap, currentPagesVisitable, semanticAppVerions, routerConfigMap, appsInstallState},
    shouldAvoidRevoking?: boolean
) => {
    const filteredClientSpecMap = clientSpecMapService.filterApps(clientSpecMap)
    const actions = await getSettleActions(
        extensionAPI,
        currentPagesVisitable,
        filteredClientSpecMap,
        routerConfigMap,
        semanticAppVerions,
        appsInstallState,
        shouldAvoidRevoking
    )
    contextAdapter.utils.fedopsLogger.interactionStarted(
        dsConstants.PLATFORM_INTERACTIONS.SETTLE_ACTIONS,
        getInteractionsParams(actions)
    )
    return actions
}

const settleOnSaveWithImmutableSnapshot = async function (
    extensionAPI: ExtensionAPI | PS['extensionAPI'],
    currentImmutableSnapshot: SnapshotDal,
    appServiceData,
    revision: string | number,
    urlData,
    shouldAvoidRevoking: boolean,
    onSuccess: Callback1<any>,
    onError: Callback1<any>
) {
    const {branchId} = appServiceData

    const data = _.omitBy(
        {
            actions: await getSettleActionsForSave(extensionAPI, appServiceData, shouldAvoidRevoking),
            siteRevision: revision,
            maybeBranchId: branchId
        },
        _.isNil
    )

    if (!_.isEmpty(data.actions)) {
        extensionAPI.logger.interactionStarted('makeSettleRequestWithImmutableSnapshot')
        makeSettleRequestWithImmutableSnapshot(currentImmutableSnapshot, data, onSuccess, onError)
        extensionAPI.logger.interactionEnded('makeSettleRequestWithImmutableSnapshot')
    } else {
        onSuccess(undefined)
    }
}

const getProvisionActionsForAllPages = (clientSpecMap: ClientSpecMap, semanticAppVersions = {}) => {
    return getProvisionActions(
        clientSpecMap,
        _.map(_.values(installedTpaAppsOnSiteService.getAppsToProvision(clientSpecMap)), ({appDefinitionId}) => ({
            appDefinitionId,
            version: semanticAppVersions[appDefinitionId]
        }))
    )
}

const settleOnLoad = async function (ps: PS, shouldAvoidRevoking?: boolean, callback?: Callback) {
    const onSuccess = getOnSuccessWithFallback('settleOnLoad', callback, function (response) {
        if (response) {
            platform.addPlatformAppsExperiments(ps, response.platformAppsExperiments)
            platform.addBlocksExperiments(ps, response.blocksExperiments)
            _.forEach(response.clientSpecMap, appData => clientSpecMapService.registerAppData(ps, appData))
        }
        if (response?.success) {
            _.forEach(response.payload.clientSpecMap, appData => clientSpecMapService.registerAppData(ps, appData))
        }
        if (callback) {
            callback()
        }
    })

    const visitableDataAllPages = visitableData.createFromPrivateServices(ps)
    const clientSpecMap = clientSpecMapService.filterApps(ps.dal.get(ps.pointers.general.getClientSpecMap()))
    const routerConfigMap = ps.dal.get(ps.pointers.routers.getRoutersConfigMapPointer())
    const siteRevision = ps.extensionAPI.siteAPI.getSiteRevision()
    const urlData = {
        appStoreUrl: ps.dal.get(ps.pointers.getInnerPointer(ps.pointers.general.getServiceTopology(), 'appStoreUrl')),
        metaSiteId: getMetaSiteId(ps),
        editorSessionId: ps.siteAPI.getEditorSessionId()
    }
    await settleImplOnLoad(
        ps,
        visitableDataAllPages,
        clientSpecMap,
        routerConfigMap,
        siteRevision,
        urlData,
        shouldAvoidRevoking,
        onSuccess
    )
}

const getAppsToUpdate = (
    clientSpecMap: ClientSpecMap,
    appsToGrant: any[] = [],
    appsToRevoke: any[] = [],
    appsState: Record<string, any> = {}
) =>
    _(clientSpecMap)
        .filter(({appDefinitionId}: any) => {
            const appState: any = _.get(appsState, appDefinitionId, {})
            return (
                appState.pendingAction === constants.APP_ACTION_TYPES.UPDATE &&
                !_.some(appsToGrant, {appDefinitionId}) &&
                !_.some(appsToRevoke, {appDefinitionId})
            )
        })
        .map((clientSpec: any) => ({
            type: 'update',
            appDefId: clientSpec.appDefinitionId,
            applicationId: _.toString(clientSpec.applicationId),
            instanceId: clientSpec.instanceId,
            appVersion: clientSpec.version
        }))
        .value()

const getAppsToAddFromAllPages = (
    appsToGrant,
    appsToRevoke,
    clientSpecMap: ClientSpecMap,
    semanticAppVersions = {},
    appsToProvision = []
) =>
    _(appsToGrant)
        .reject(
            ({appDefinitionId}) =>
                _.some(appsToRevoke, {appDefinitionId}) || _.some(appsToProvision, {appDefId: appDefinitionId})
        )
        .map(({appDefinitionId, applicationId, instanceId, version, appFields}) => {
            const appVersion = appFields?.installedVersion || semanticAppVersions[appDefinitionId] || version
            return _.omitBy(
                {
                    type: 'add',
                    appDefId: appDefinitionId,
                    applicationId: _.toString(applicationId),
                    instanceId,
                    appVersion
                },
                _.isUndefined
            )
        })
        .value()

const getAppsToRemove = appsToRevoke =>
    _.map(appsToRevoke, ({applicationId}) => ({
        type: 'remove',
        applicationId: _.toString(applicationId)
    }))

const getAppsToDismiss = () =>
    _.map(pendingAppsService.getAppsToDismiss(), appDefId => ({
        type: 'dismiss',
        appDefId
    }))

const processAppsAndGenerateActions = async (
    extensionAPI: ExtensionAPI | PS['extensionAPI'],
    clientSpecMap: ClientSpecMap,
    visitableDataAllPages,
    routerConfigMap,
    options: {
        shouldAvoidRevoking?: boolean
        excludeHybrid?: boolean
        removeProvisionedFromGrant?: boolean
        includePendingApps?: boolean
        shouldGetProvisionForAllPages?: boolean
    } = {},
    semanticAppVersions?: any,
    providedAppsState?: AppsInstallStateMap
) => {
    const {
        shouldAvoidRevoking = false,
        excludeHybrid = false,
        removeProvisionedFromGrant = false,
        includePendingApps = false,
        shouldGetProvisionForAllPages = false
    } = options
    const appsState = providedAppsState || getAppState(extensionAPI)

    const {grant, revoke} = await installedTpaAppsOnSiteService.getAppsToGrantAndRevoke(
        extensionAPI,
        clientSpecMap,
        visitableDataAllPages,
        {shouldAvoidRevoking, excludeHybrid},
        appsState
    )

    let appsToGrant = [...grant]
    let appsToProvision = []

    if (removeProvisionedFromGrant) {
        appsToGrant = _.concat(appsToGrant, _.filter(clientSpecMap, 'notProvisioned' as any))
    }

    if (includePendingApps) {
        appsToGrant = _.concat(appsToGrant, pendingAppsService.getAppsToAdd())
    }

    if (shouldGetProvisionForAllPages) {
        appsToProvision = _.concat(getProvisionActionsForAllPages(clientSpecMap, semanticAppVersions))
    }

    const {allActions} = categorizeAppsIntoActions(
        appsToGrant,
        revoke,
        clientSpecMap,
        semanticAppVersions,
        appsToProvision,
        appsState
    )

    return {
        appsToProvision,
        allActions
    }
}

const categorizeAppsIntoActions = (
    appsToGrant: any[],
    appsToRevoke: any[],
    clientSpecMap: ClientSpecMap,
    semanticAppVersions?: any,
    appsToProvision?: any[],
    appsState: any = {}
) => {
    const appsToAdd = getAppsToAddFromAllPages(
        appsToGrant,
        appsToRevoke,
        clientSpecMap,
        semanticAppVersions,
        appsToProvision
    )
    const appsToRemove = getAppsToRemove(appsToRevoke)
    const appsToDismiss = getAppsToDismiss()
    const appsToUpdate = getAppsToUpdate(clientSpecMap, appsToGrant, appsToRevoke, appsState)

    return {
        allActions: _.concat(appsToAdd, appsToRemove, appsToDismiss, appsToUpdate)
    }
}

const getAppState = (extensionAPI: ExtensionAPI | PS['extensionAPI']) =>
    extensionAPI.appsInstallState.getAllAppsInstallStatus()

const settleImplOnLoad = async (
    ps: PS,
    visitableDataAllPages,
    clientSpecMap: ClientSpecMap,
    routerConfigMap,
    siteRevision: string,
    urlData,
    shouldAvoidRevoking,
    callback
) => {
    const branchId = ps.extensionAPI.siteAPI.getBranchId()

    const {allActions} = await processAppsAndGenerateActions(
        ps.extensionAPI,
        clientSpecMap,
        visitableDataAllPages,
        routerConfigMap,
        {
            shouldAvoidRevoking,
            excludeHybrid: true
        }
    )

    const data = _.omitBy(
        {
            actions: allActions,
            siteRevision,
            maybeBranchId: branchId
        },
        _.isNil
    )

    if (!_.isEmpty(data.actions)) {
        ps.extensionAPI.appsInstallState.reportStateDifferenceByActions(clientSpecMap, data.actions, 'settleOnLoad')
        queueMakingSettleRequestWithPSAsync(ps, urlData, data, context.load)
            .then(callback)
            .catch(e => {
                ps.extensionAPI.logger.captureError(
                    getReportableFromError(e, {
                        errorType: 'settleImplOnLoadError',
                        message: `Failed settleImplOnLoad`,
                        tags: {
                            provisioningOnSuccess: true
                        }
                    })
                )

                console.error(e)
                callback()
            })
    } else {
        callback()
    }
}

const getSettleActionsFromAllPages = async (
    extensionAPI,
    currentPagesVisitable,
    clientSpecMap: ClientSpecMap,
    routerConfigMap,
    semanticAppVersions,
    appsState: AppsInstallStateMap,
    shouldAvoidRevoking: boolean
) => {
    const {allActions, appsToProvision} = await processAppsAndGenerateActions(
        extensionAPI,
        clientSpecMap,
        currentPagesVisitable,
        routerConfigMap,
        {
            shouldAvoidRevoking,
            shouldGetProvisionForAllPages: true,
            removeProvisionedFromGrant: true,
            includePendingApps: true
        },
        semanticAppVersions,
        appsState
    )

    return _.concat(appsToProvision, allActions)
}

const getNewApplicationIdStartingFrom1k = function (clientSpecMap: ClientSpecMap): number {
    const currentLargestId = clientSpecMapService.getLargestApplicationId(clientSpecMap)
    const newGeneratedApplicationId = provisionUtils.generateAppFlowsLargestAppId(currentLargestId)
    return newGeneratedApplicationId
}

const settleOnLoadAsync = (ps: PS, shouldAvoidRevoking?: boolean) =>
    new Promise<void>(res => settleOnLoad(ps, shouldAvoidRevoking, res))

export default {
    provision,
    provisionAsync,
    update,
    settleOnSaveWithImmutableSnapshot,
    getSettleActionsForFullSave,
    getSettleActionsForSave,
    settleOnLoad,
    settleOnLoadAsync,
    waitForSettleQueue: settleQueue.wait
}
